/** Загрузка своих книг на сервер: перетащил файлы — они в каталоге. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, formatSize, uploadBook } from '../api'
import type { UploadEntry, UploadResult, UploadsInfo } from '../api'
import { Spinner } from '../components/Spinner'

/** Файл в очереди отправки и его судьба. */
interface Pending {
  key: number
  file: File
  sent: number
  state: 'waiting' | 'sending' | 'done' | 'failed'
  result?: UploadResult
  error?: string
}

let nextKey = 1

export function UploadsPage() {
  const navigate = useNavigate()
  const [info, setInfo] = useState<UploadsInfo | null>(null)
  const [error, setError] = useState('')
  const [queue, setQueue] = useState<Pending[]>([])
  const [over, setOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Файлы уходят по одному: так полоса хода честная, а ошибка в одном
  // файле не отменяет остальные. Флаг защищает от параллельного запуска.
  const sending = useRef(false)

  const load = useCallback(() => {
    api
      .uploads()
      .then((data) => {
        setInfo(data)
        setError('')
      })
      .catch(() => setError('не удалось получить список загрузок'))
  }, [])

  useEffect(load, [load])

  const patch = (key: number, changes: Partial<Pending>) =>
    setQueue((prev) => prev.map((item) => (item.key === key ? { ...item, ...changes } : item)))

  const pump = useCallback(async () => {
    if (sending.current) return
    sending.current = true
    try {
      for (;;) {
        // Очередь читается заново на каждом шаге: пока файл шёл, могли
        // добавить ещё.
        const next = await new Promise<Pending | undefined>((resolve) =>
          setQueue((prev) => {
            resolve(prev.find((item) => item.state === 'waiting'))
            return prev
          }),
        )
        if (!next) break
        patch(next.key, { state: 'sending' })
        try {
          const result = await uploadBook(next.file, (sent) => patch(next.key, { sent }))
          patch(next.key, {
            state: result.ok ? 'done' : 'failed',
            result,
            error: result.ok ? undefined : result.error,
            sent: next.file.size,
          })
        } catch (exc) {
          patch(next.key, { state: 'failed', error: describe(exc) })
        }
      }
    } finally {
      sending.current = false
      load()
    }
  }, [load])

  const enqueue = (files: FileList | File[]) => {
    const accepted = info?.formats ?? []
    const added: Pending[] = []
    for (const file of Array.from(files)) {
      const lowered = file.name.toLowerCase()
      const fits = accepted.length === 0 || accepted.some((suffix) => lowered.endsWith(suffix))
      const item: Pending = { key: nextKey++, file, sent: 0, state: fits ? 'waiting' : 'failed' }
      if (!fits) item.error = 'формат не поддерживается'
      else if (info && file.size > info.max_bytes) {
        item.state = 'failed'
        item.error = `больше допустимых ${formatSize(info.max_bytes)}`
      }
      added.push(item)
    }
    if (added.length === 0) return
    setQueue((prev) => [...added, ...prev])
    void pump()
  }

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setOver(false)
    if (event.dataTransfer.files.length) enqueue(event.dataTransfer.files)
  }

  const remove = async (entry: UploadEntry) => {
    const titles = entry.books.map((book) => book.title).join(', ')
    const warning =
      `Удалить «${entry.name}» с сервера?\n\n` +
      (titles ? `Из каталога пропадёт: ${titles}.` : '') +
      ' Позиции чтения других файлов той же книги сохранятся.'
    if (!window.confirm(warning)) return
    try {
      await api.deleteUpload(entry.name)
      load()
    } catch (exc) {
      setError(describe(exc))
    }
  }

  const accept = (info?.formats ?? []).join(',')

  return (
    <>
      <div className="topbar">
        <button className="btn btn--ghost" onClick={() => navigate('/')}>
          ← Каталог
        </button>
        <span className="topbar__brand">Загрузка книг</span>
        <span className="topbar__spacer" />
        <button className="btn" onClick={load}>
          Обновить
        </button>
      </div>

      <div className="content status">
        {error && <p className="error">{error}</p>}
        {!info && !error && <Spinner />}

        {info && !info.enabled && (
          <p className="dim">
            Загрузка книг на этом сервере отключена. Включается в chita.toml:{' '}
            <code>allow_uploads = true</code>.
          </p>
        )}

        {info?.enabled && (
          <>
            <div
              className={`dropzone${over ? ' dropzone--over' : ''}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault()
                setOver(true)
              }}
              onDragLeave={() => setOver(false)}
              onDrop={onDrop}
            >
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={accept}
                onChange={(event) => {
                  if (event.target.files) enqueue(event.target.files)
                  event.target.value = ''
                }}
              />
              <strong>Перетащите книги сюда или нажмите, чтобы выбрать</strong>
              <span className="dropzone__hint">
                Принимаются {describeFormats(info.formats)}. Файл — до{' '}
                {formatSize(info.max_bytes)}. Книга попадает в каталог сразу: fb2, epub и txt
                можно читать, остальное — скачивать.
              </span>
            </div>

            {queue.length > 0 && (
              <section>
                <h3>Отправка</h3>
                <div className="upload-list">
                  {queue.map((item) => (
                    <PendingRow key={item.key} item={item} />
                  ))}
                </div>
              </section>
            )}

            <section>
              <h3>Загруженные</h3>
              {info.items.length === 0 ? (
                <p className="dim">Пока ничего не присылали.</p>
              ) : (
                <div className="upload-list">
                  {info.items.map((entry) => (
                    <UploadedRow key={entry.name} entry={entry} onRemove={() => void remove(entry)} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </>
  )
}

function PendingRow({ item }: { item: Pending }) {
  const percent = item.file.size ? Math.round((item.sent / item.file.size) * 100) : 0
  const result = item.result
  let note = ''
  if (item.state === 'waiting') note = 'в очереди'
  else if (item.state === 'sending') note = percent < 100 ? `${percent} %` : 'разбирается…'
  else if (item.state === 'failed') note = item.error ?? 'ошибка'
  else if (result?.duplicate) note = 'уже была на сервере'
  else note = 'принята'

  return (
    <div className={`upload-item${item.state === 'failed' ? ' upload-item--error' : ''}`}>
      <span className="upload-item__name">{item.file.name}</span>
      <span className="upload-item__side">
        {formatSize(item.file.size)} · {note}
      </span>
      {item.state === 'sending' && (
        <div className="progress">
          <div className="progress__bar" style={{ width: `${percent}%` }} />
        </div>
      )}
      {result?.books && result.books.length > 0 && <BookLinks books={result.books} />}
      {result?.warnings && result.warnings.length > 0 && (
        <span className="upload-item__note">
          Файл разобран не полностью: {result.warnings[0]}
        </span>
      )}
    </div>
  )
}

function UploadedRow({ entry, onRemove }: { entry: UploadEntry; onRemove: () => void }) {
  const when = entry.uploaded_at
    ? new Date(entry.uploaded_at * 1000).toLocaleDateString('ru-RU')
    : ''
  return (
    <div className="upload-item">
      <span className="upload-item__name">{entry.name}</span>
      <span className="upload-item__side">
        {formatSize(entry.size)}
        {when && ` · ${when}`}
        {entry.uploaded_by && !entry.mine && ` · ${entry.uploaded_by}`}
        {entry.can_delete && (
          <>
            {' · '}
            <button className="linkish" onClick={onRemove} title="Убрать с сервера">
              удалить
            </button>
          </>
        )}
      </span>
      <BookLinks books={entry.books} />
    </div>
  )
}

function BookLinks({ books }: { books: UploadEntry['books'] }) {
  return (
    <div className="upload-item__books">
      {books.map((book) => (
        <Link key={book.id} to={book.readable ? `/read/${book.id}` : `/book/${book.id}`}>
          {book.authors.length > 0 && (
            <span className="dim">{book.authors.map((a) => a.name).join(', ')} — </span>
          )}
          {book.title}
        </Link>
      ))}
    </div>
  )
}

/** «fb2, epub, txt, pdf… и архивы zip/7z» — список без точек и без повторов. */
function describeFormats(formats: string[]): string {
  const books = formats
    .filter((suffix) => suffix !== '.zip' && suffix !== '.7z' && suffix !== '.fb2.zip')
    .map((suffix) => suffix.slice(1))
  const archives = formats.filter((suffix) => suffix === '.zip' || suffix === '.7z')
  let text = books.join(', ')
  if (archives.length) text += `, а также архивы ${archives.map((s) => s.slice(1)).join(' и ')}`
  return text
}

function describe(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return 'не удалось отправить'
}
